import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pmGet, pmPost } from '../api';
import { PHASE_COLORS, mvpColor } from './timelineColors';
import './RoadmapDefinition.css';
import { SelectField, PROMPT, UNSET } from '@platform/ui-kit';

/**
 * Project Creation.
 *
 * Cloned from the source's `definition.ejs`. Renamed from "Roadmap Definition" with §14's
 * restructure, which moves it under Portfolio → Project Setup and renames both the menu entry
 * and this heading together (§14.2 #4 — a menu item labelled one thing landing on a page
 * headed another is the defect that section names).
 *
 * THE SCREEN KEY DID NOT MOVE. `delivery_project_screen_permissions` rows are keyed by it,
 * and changing one silently orphans every grant ever made against the screen (§14.1.1). Only
 * the label, the heading and the position changed; the old route redirects (§14.3).
 *
 * CLONED from `ai_readiness_template/views/roadmap-execution/definition.ejs`, read end to end
 * before any of this was written (CLAUDE.md §1.2.1). Column order, widths, labels, badge
 * wording, empty state and the methodology toggle are the source's; every CSS value is in the
 * stylesheet beside this file, traceable to the line it came from.
 *
 * ── WHAT THE SCREEN IS FOR ─────────────────────────────────────────────────────────────
 *
 * It brings opportunities into the execution roadmap. One collapsed line per PROJECT; expand
 * it to see its opportunities, tick the ones that are real, and press Generate project on that
 * project's row. Expanding also draws one combined Gantt for the whole programme.
 *
 * ── TWO THINGS THE SOURCE DOES THAT ARE EASY TO GET WRONG ──────────────────────────────
 *
 * 1. AN OPPORTUNITY ALREADY IN EXECUTION IS STILL SELECTABLE. Being in execution is
 *    INFORMATION, not a lock — the source says so in a comment, and change record §7 item 12
 *    lists re-enabling it as a fix. Re-generating updates it.
 *
 * 2. AFTER A METHODOLOGY, CADENCE OR DATE CHANGE THE CANDIDATES ARE RE-PULLED, and the open
 *    projects are re-opened afterwards. The MVP/sprint segments are generated SERVER-SIDE, so
 *    re-rendering the object already in memory leaves `agile.mvps` empty and the chart silently
 *    falls back to the Waterfall phases. That was one of the three Gantt bugs in change record
 *    §7, and it is why these handlers look heavier than a local state update would.
 *
 * THE DELIVERY PROJECTS PANEL IS NOT HERE, and that is the source's current state rather than
 * an omission: `switchTab()` sets `standalonePanel` to `display:none` unconditionally — "One
 * screen — there are no tabs any more" (change record §7 item 1). Only the candidates table
 * renders.
 */

/**
 * A methodology with no phases: an explicit "None", or nothing chosen yet.
 *
 * Both read as flat on this screen, but they are DIFFERENT statements — "None" is a decision
 * that the initiative has no delivery lifecycle, null is nobody having chosen. The toggle
 * shows which of the two it is; everything that DRAWS treats them alike.
 */
const isFlatMeth = (m: string | null) => {
  const v = (m ?? '').trim();
  return v === '' || v === 'None';
};

/*
 * The phase and MVP colours come from `timelineColors.ts`, which holds the SOURCE's values —
 * the same file Timeline Finalization and its Gantt use.
 *
 * This screen carried its own copy of a STRONGER set. Two copies of one palette in one product
 * is exactly how a phase ends up a different colour on two screens, and the owner asked for
 * the AI Readiness values throughout. Nothing else about this screen changed.
 */

interface Phase {
  name: string;
  label: string;
  start_date: string;
  end_date: string;
}
interface AgileSprint { start?: string; end?: string }
interface AgileMvp { sprints?: AgileSprint[] }
interface Agile {
  mvps?: AgileMvp[];
  mvpCount?: number;
  sprintsPerMvp?: number;
  sprintWeeks?: number;
}
interface Candidate {
  opportunity_id: string;
  project_id: string | null;
  description: string | null;
  status: string | null;
  delivery_pathway: string;
  phase_source: string;
  in_execution: boolean;
  delivery_project_id: string | null;
  delivery_project_description: string | null;
  project_description: string;
  methodology: string | null;
  agile: Agile | null;
  planned_start_date: string | null;
  planned_end_date: string | null;
  total_duration_days: number | null;
  phases: Phase[];
}

/** One bar on the Gantt. */
interface Seg {
  start_date: string;
  end_date: string;
  name?: string;
  color?: string;
}

/** A date at UTC midnight, so a bar never shifts a day west of Greenwich. */
const pd = (s: unknown): number | null => {
  const v = String(s ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const t = Date.parse(v + 'T00:00:00Z');
  return Number.isNaN(t) ? null : t;
};
const isWeekend = (ms: number) => {
  const d = new Date(ms).getUTCDay();
  return d === 0 || d === 6;
};

/**
 * One opportunity's bar segments — the source's `pgSegsOf`.
 *
 * Agile draws its SPRINTS coloured by MVP; Waterfall draws the five phases. The methodology
 * decides, which is what change record §7 item 6 is about: the chart must follow the
 * methodology rather than always drawing phases.
 */
function segsOf(c: Candidate): Seg[] {
  /*
   * A PHASE-LESS INITIATIVE IS ONE BAR, in the Assessment & Planning colour — change record
   * §4.1: "single Gantt bar in the Assessment & Planning colour".
   *
   * It is drawn from the initiative's own planned dates rather than from `phases`, because a
   * "None" initiative has had its five phase_* columns blanked and has no phase rows to draw.
   * Without this it rendered as an empty lane, which reads as missing data rather than as a
   * deliberate choice.
   */
  if (isFlatMeth(c.methodology)) {
    return c.planned_start_date && c.planned_end_date
      ? [
          {
            start_date: c.planned_start_date,
            end_date: c.planned_end_date,
            name: 'Delivery',
            color: PHASE_COLORS.Assessment_Planning,
          },
        ]
      : [];
  }
  if (c.methodology === 'Agile' && c.agile?.mvps?.length) {
    const segs: Seg[] = [];
    c.agile.mvps.forEach((m, mi) => {
      (m.sprints ?? []).forEach((s) => {
        if (s.start && s.end) {
          segs.push({ start_date: s.start, end_date: s.end, color: mvpColor(mi) });
        }
      });
    });
    return segs;
  }
  return (c.phases ?? [])
    .filter((p) => p.start_date && p.end_date)
    .map((p) => ({ start_date: p.start_date, end_date: p.end_date, name: p.name }));
}

/** Waterfall / Agile counts for the collapsed project line — the source's `methSummary`. */
function methSummary(items: Candidate[]): string {
  const a = items.filter((x) => x.methodology === 'Agile').length;
  const w = items.filter((x) => x.methodology === 'Waterfall').length;
  /* "None" is counted separately from "not set": one is a decision, the other is an absence,
     and collapsing them would hide which initiatives still need a choice. */
  const nn = items.filter((x) => x.methodology === 'None').length;
  const n = items.length - a - w - nn;
  const out: string[] = [];
  if (w) out.push(`${w} WF`);
  if (a) out.push(`${a} AG`);
  if (nn) out.push(`${nn} None`);
  if (n) out.push(`${n} not set`);
  return out.join(' · ');
}

interface Group {
  pid: string;
  pdesc: string;
  items: Candidate[];
}

export function RoadmapDefinition() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [projectFilter, setProjectFilter] = useState(UNSET);
  const [openPids, setOpenPids] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /* Generate Project modal: which project row opened it, and the name being typed. */
  const [modalPid, setModalPid] = useState<string | null>(null);
  const [deliveryName, setDeliveryName] = useState('');
  const [modalErr, setModalErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await pmGet<{ success: boolean; candidates?: Candidate[]; error?: string }>(
        '/api/definition/candidates',
      );
      if (!d.success) throw new Error(d.error ?? 'Could not load candidates');
      setCandidates(d.candidates ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* Rows after the project filter, grouped by project, projects in id order. */
  const groups = useMemo<Group[]>(() => {
    /* UNSET (nothing chosen yet) and '' ("All Projects") both mean "do not filter" — so what
       the grid shows on arrival is exactly what it showed before. Only the field's own text
       changes, from "All Projects" to the prompt. */
    const rows = candidates.filter(
      (c) => !projectFilter || projectFilter === UNSET || c.project_id === projectFilter,
    );
    const by = new Map<string, Group>();
    for (const c of rows) {
      const pid = String(c.project_id ?? '');
      if (!by.has(pid)) {
        by.set(pid, { pid, pdesc: c.project_description ?? '', items: [] });
      }
      by.get(pid)!.items.push(c);
    }
    return [...by.values()].sort((a, b) => a.pid.localeCompare(b.pid));
  }, [candidates, projectFilter]);

  /* Id AND description, so the picker can show the id in bold followed by its description.
     The ids were already unique and sorted; this keeps both and carries the first description
     seen for each. */
  const projectOptions = useMemo(() => {
    const by = new Map<string, string>();
    for (const c of candidates) {
      const pid = String(c.project_id ?? '');
      if (pid && !by.has(pid)) by.set(pid, c.project_description ?? '');
    }
    return [...by.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([id, description]) => ({ id, description }));
  }, [candidates]);

  /* ── selection ─────────────────────────────────────────────────────────────────────── */
  const selectedIn = (g: Group) => g.items.filter((c) => selected.has(c.opportunity_id));

  const toggleOne = (oid: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(oid);
      else next.delete(oid);
      return next;
    });

  /** The project checkbox ticks or clears its whole group, and opens it if it was closed. */
  const toggleGroupSelect = (g: Group, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of g.items) {
        if (on) next.add(c.opportunity_id);
        else next.delete(c.opportunity_id);
      }
      return next;
    });
    if (on && !openPids.has(g.pid)) toggleProject(g.pid);
  };

  const toggleProject = (pid: string) =>
    setOpenPids((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });

  const expandAll = (expand: boolean) =>
    setOpenPids(expand ? new Set(groups.map((g) => g.pid)) : new Set());

  /* ── mutations ─────────────────────────────────────────────────────────────────────── */

  /**
   * Re-pull after any change that the SERVER recomputes, keeping the open projects open.
   *
   * See the header: MVP/sprint segments are generated server-side, so a local state update
   * leaves the chart drawing the previous cadence.
   */
  const reloadKeepingOpen = async (alsoOpen?: string | null) => {
    const keep = new Set(openPids);
    if (alsoOpen) keep.add(alsoOpen);
    await load();
    setOpenPids(keep);
  };

  const setRowMethodology = async (c: Candidate, meth: 'Waterfall' | 'Agile' | 'None') => {
    /* Clicking the ACTIVE one clears it — the source's rule. */
    const next = c.methodology === meth ? null : meth;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/api/definition/opportunity-methodology',
        { opportunity_id: c.opportunity_id, methodology: next },
      );
      if (!d.success) throw new Error(d.error ?? 'unknown');
      await reloadKeepingOpen(c.project_id);
    } catch (e) {
      setError(`Could not save methodology: ${(e as Error).message}`);
    }
  };

  const setAgileCfg = async (c: Candidate, sprintWeeks: number, sprintsPerMvp: number) => {
    /* Not stored until the opportunity is in a delivery project — the execution row is where
       the cadence lives. The source says so on screen rather than failing silently. */
    if (!c.delivery_project_id) {
      setCandidates((prev) =>
        prev.map((x) =>
          x.opportunity_id === c.opportunity_id
            ? { ...x, agile: { ...(x.agile ?? {}), sprintWeeks, sprintsPerMvp } }
            : x,
        ),
      );
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/api/definition/opportunity-agile-config',
        {
          delivery_project_id: c.delivery_project_id,
          opportunity_id: c.opportunity_id,
          sprints_per_mvp: sprintsPerMvp,
          sprint_weeks: sprintWeeks,
        },
      );
      if (!d.success) throw new Error(d.error ?? 'unknown');
      await reloadKeepingOpen(c.project_id);
    } catch (e) {
      setError(`Could not save the Agile cadence: ${(e as Error).message}`);
    }
  };

  const setOppDates = async (c: Candidate, start: string, end: string) => {
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/api/definition/opportunity-dates',
        { opportunity_id: c.opportunity_id, start_date: start || null, end_date: end || null },
      );
      if (!d.success) throw new Error(d.error ?? 'unknown');
      /* The duration and the bars come back from the app's own rules rather than being
         recomputed in the browser — the source's reasoning, and it keeps one definition. */
      await reloadKeepingOpen(c.project_id);
    } catch (e) {
      setError(`Could not save dates: ${(e as Error).message}`);
    }
  };

  /* ── Generate Project ──────────────────────────────────────────────────────────────── */
  const openGenerate = (g: Group) => {
    const ids = selectedIn(g);
    if (!ids.length) return;
    setModalPid(g.pid);
    setModalErr(null);
    /* Default the delivery project to the source project's own id and description, so the
       box is never blank. */
    setDeliveryName(g.pdesc ? `${g.pid} — ${g.pdesc}` : g.pid);
  };

  const confirmGenerate = async () => {
    const g = groups.find((x) => x.pid === modalPid);
    if (!g) return;
    const ids = selectedIn(g).map((c) => c.opportunity_id);
    if (!ids.length) {
      setModalPid(null);
      return;
    }
    const combined = deliveryName.trim();
    if (!combined) {
      setModalErr('Please enter a Project.');
      return;
    }
    /*
     * Split on the FIRST dash SURROUNDED BY SPACES, not on any dash.
     *
     * Ids legitimately contain dashes — PRJ-LS-03, CRL-01_Small_Molecule_1, STD-001 — so
     * splitting on any dash would cut them in half. The source's regex, kept exactly.
     */
    const parts = /^(.*?)\s+[—–-]\s+([\s\S]*)$/.exec(combined);
    const did = (parts ? parts[1]! : combined).trim();
    const ddesc = (parts ? parts[2]! : '').trim();
    if (!did) {
      setModalErr('Please enter a Project.');
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; added?: number; error?: string }>(
        '/api/definition/add',
        { opportunity_ids: ids, delivery_project_id: did, delivery_project_description: ddesc },
      );
      if (!d.success) {
        setModalErr(d.error ?? 'Failed to add');
        return;
      }
      setModalPid(null);
      setSelected(new Set());
      await load();
    } catch (e) {
      setModalErr((e as Error).message || 'Request failed');
    }
  };

  return (
    <div className="rd-page">
      {/* ── Fixed header ─────────────────────────────────────────────────────────────── */}
      <div className="rd-head">
        <div className="rd-title-wrap">
          {/*
            §14.2 #4 — THE PAGE HEADING MUST MATCH THE MENU LABEL. This read "Roadmap
            Definition — Execution" while the menu, the User Guide, Timeline Finalization's
            cross-link and Work Item Definition all say "Roadmap Definition". A menu item that
            lands on a page headed something else makes the reader wonder whether they clicked
            the right thing.

            The menu label is the owner's own naming (2026-08-11), so the heading follows it
            rather than the other way round. The suffix was disambiguating nothing: the group
            and the leaf that carry this name are already distinct in the nav.
          */}
          <h1 className="page-header__title">Project Creation</h1>
          <p className="rd-sub">
            Bring In-Development opportunities&rsquo; planned timelines into the execution
            roadmap. Opportunities already in execution are shown checked and greyed.
          </p>
        </div>

        {error ? <div className="rd-err">{error}</div> : null}

        <div className="rd-controls">
          {/*
            The platform's one picker — `SelectField` from the UI kit, whose metrics were
            taken from this screen's own control. `lg` is 380px against the 180px this used
            to be: project names are descriptive, and the old width clipped most of them.
          */}
          <SelectField
            label="Project"
            width="lg"
            value={projectFilter}
            onChange={setProjectFilter}
            placeholder={PROMPT.project}
            options={[
              // An empty value is a real choice here, not an absent one.
              { value: '', label: 'All Projects' },
              ...projectOptions.map((p) => ({ value: p.id, id: p.id, description: p.description })),
            ]}
          />
          <button type="button" className="rd-btn grey" title="Refresh" onClick={() => void load()}>
            ↻ Refresh
          </button>
          <button type="button" className="rd-btn" onClick={() => expandAll(true)}>
            ⊞ Expand all
          </button>
          <button type="button" className="rd-btn" onClick={() => expandAll(false)}>
            ⊟ Collapse all
          </button>
          <span className="rd-hint">
            Expand a project, tick its opportunities, then <strong>Generate project</strong> on
            that project&rsquo;s row.
          </span>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────────────────── */}
      <div className="rd-body">
        <div className="rd-card">
          {/* The header is its own table so it never scrolls with the body — CLAUDE.md's
              "a screen header never scrolls", and the source's own structure. */}
          <div className="rd-thead-wrap">
            <table className="rd-tbl">
              <colgroup>
                <col style={{ width: 56 }} />
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 88 }} />
                <col style={{ width: 170 }} />
                <col style={{ width: 224 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ width: 56 }} />
                  <th className="left">Project / Opportunity</th>
                  <th style={{ width: 150 }}>Planned Start Date</th>
                  <th style={{ width: 150 }}>Planned End Date</th>
                  <th style={{ width: 88 }}>Duration</th>
                  <th style={{ width: 170 }}>Execution</th>
                  <th style={{ width: 224 }}>Methodology</th>
                </tr>
              </thead>
            </table>
          </div>

          <div className="rd-tbody-wrap">
            {loading ? (
              <div className="rd-empty">Loading…</div>
            ) : !groups.length ? (
              <div className="rd-empty">
                <div className="rd-empty-icon">🚀</div>
                <h3>No Opportunities to Define</h3>
                <p>
                  In-Development opportunities with a planned timeline will appear here, ready to
                  bring into execution.
                </p>
              </div>
            ) : (
              <table className="rd-tbl">
                <colgroup>
                  <col style={{ width: 56 }} />
                  <col />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 170 }} />
                  <col style={{ width: 224 }} />
                </colgroup>
                <tbody>
                  {groups.map((g) => {
                    const open = openPids.has(g.pid);
                    const sel = selectedIn(g);
                    const dates = g.items
                      .flatMap((c) => [c.planned_start_date, c.planned_end_date])
                      .filter(Boolean)
                      .sort() as string[];
                    const combStart = dates.length ? dates[0]! : '-';
                    const combEnd = dates.length ? dates[dates.length - 1]! : '-';
                    const inExec = g.items.filter((c) => c.in_execution).length;
                    const allChecked = sel.length > 0 && sel.length === g.items.length;

                    return (
                      <Fragment key={g.pid}>
                        <tr className="rd-projrow">
                          <td className="chk">
                            <input
                              type="checkbox"
                              title="Select all opportunities in this project"
                              checked={allChecked}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate =
                                    sel.length > 0 && sel.length < g.items.length;
                                }
                              }}
                              onChange={(e) => toggleGroupSelect(g, e.target.checked)}
                            />
                          </td>
                          <td className="name">
                            <div className="rd-projline">
                              <span
                                className="rd-projname"
                                onClick={() => toggleProject(g.pid)}
                              >
                                <span className="rd-caret">{open ? '▼' : '▶'}</span>📁 {g.pid}
                                {g.pdesc ? ` — ${g.pdesc}` : ''}{' '}
                                <span className="rd-projcount">
                                  ({g.items.length} opportunit{g.items.length === 1 ? 'y' : 'ies'})
                                </span>
                              </span>
                              {/* Say WHY it is greyed — an unexplained disabled button reads
                                  as broken. The source's own titles. */}
                              <button
                                type="button"
                                className="rd-gen"
                                disabled={!sel.length}
                                title={
                                  sel.length
                                    ? `Generate a project from the ${sel.length} selected opportunit${sel.length === 1 ? 'y' : 'ies'}`
                                    : 'Tick at least one opportunity below (or the project checkbox on the left) to enable this.'
                                }
                                onClick={() => openGenerate(g)}
                              >
                                ➕ Generate project
                              </button>
                            </div>
                          </td>
                          <td>{combStart}</td>
                          <td>{combEnd}</td>
                          <td>{sel.length} selected</td>
                          <td>{inExec ? `${inExec} in execution` : '—'}</td>
                          <td className="rd-methsum">{methSummary(g.items)}</td>
                        </tr>

                        {open
                          ? g.items.map((c) => (
                              <OpportunityRow
                                key={c.opportunity_id}
                                c={c}
                                checked={selected.has(c.opportunity_id)}
                                onToggle={(on) => toggleOne(c.opportunity_id, on)}
                                onDates={(s, e) => void setOppDates(c, s, e)}
                                onMethodology={(m) => void setRowMethodology(c, m)}
                                onCadence={(sw, spm) => void setAgileCfg(c, sw, spm)}
                              />
                            ))
                          : null}

                        {open ? (
                          <tr className="rd-ganttrow">
                            <td colSpan={7}>
                              <div className="rd-gantt">
                                <ProjectGantt items={g.items} />
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── Generate Project modal ───────────────────────────────────────────────────── */}
      {modalPid ? (
        <div className="rd-modal">
          <div className="rd-modal-card">
            <div className="rd-modal-head">
              <h2>Generate Project</h2>
              <button type="button" className="rd-modal-x" onClick={() => setModalPid(null)}>
                ×
              </button>
            </div>
            <div className="rd-modal-body">
              <p className="intro">
                Combine the selected opportunities into a <strong>Project</strong> for
                execution. The <strong>ID</strong> is the key for this execution roadmap — it can
                be anything.
              </p>
              <label className="rd-modal-lbl">Project *</label>
              <input
                type="text"
                className="rd-modal-in"
                maxLength={260}
                placeholder="e.g., 3617 — Lease Accounting Process"
                value={deliveryName}
                onChange={(e) => setDeliveryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void confirmGenerate();
                  }
                }}
              />
              <div className="rd-modal-sublbl">Opportunities to include:</div>
              <div className="rd-modal-list">
                {(groups.find((x) => x.pid === modalPid)?.items ?? [])
                  .filter((c) => selected.has(c.opportunity_id))
                  .map((c) => (
                    <div key={c.opportunity_id}>
                      <strong>{c.opportunity_id}</strong>{' '}
                      {(c.description ?? '').substring(0, 70)}
                    </div>
                  ))}
              </div>
              {modalErr ? <div className="rd-modal-err">{modalErr}</div> : null}
              <div className="rd-modal-foot">
                <button
                  type="button"
                  className="rd-modal-cancel"
                  onClick={() => setModalPid(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rd-modal-go"
                  onClick={() => void confirmGenerate()}
                >
                  Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────────────── */

function OpportunityRow({
  c,
  checked,
  onToggle,
  onDates,
  onMethodology,
  onCadence,
}: {
  c: Candidate;
  checked: boolean;
  onToggle: (on: boolean) => void;
  onDates: (start: string, end: string) => void;
  onMethodology: (m: 'Waterfall' | 'Agile' | 'None') => void;
  onCadence: (sprintWeeks: number, sprintsPerMvp: number) => void;
}) {
  const done = c.in_execution;
  const dur = c.total_duration_days ? `${c.total_duration_days} days` : '-';
  const sw = c.agile?.sprintWeeks ?? 2;
  const spm = c.agile?.sprintsPerMvp ?? 4;

  return (
    <tr className={`rd-opprow${done ? ' done' : ''}`}>
      <td className="chk">
        {/* Selectable either way — being in execution is INFORMATION, not a lock. */}
        <input
          type="checkbox"
          checked={checked}
          title={done ? 'Already in execution — re-generating will update it' : undefined}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </td>
      <td className="name">
        <span className="rd-dot" />
        <strong>{c.opportunity_id}</strong>{' '}
        <span className="rd-oppdesc">{(c.description ?? '').substring(0, 70)}</span>
      </td>
      <td>
        <input
          type="date"
          className="dt-in"
          defaultValue={c.planned_start_date ?? ''}
          onChange={(e) =>
            onDates(e.target.value, c.planned_end_date ?? '')
          }
        />
      </td>
      <td>
        <input
          type="date"
          className="dt-in"
          defaultValue={c.planned_end_date ?? ''}
          onChange={(e) => onDates(c.planned_start_date ?? '', e.target.value)}
        />
      </td>
      <td>{dur}</td>
      <td>
        {done ? (
          <span className="rd-badge exec" title={c.delivery_project_description ?? ''}>
            ✓ {c.delivery_project_id ?? 'In Execution'}
          </span>
        ) : (
          <span className="rd-badge avail">Available</span>
        )}
      </td>
      <td>
        <span className="mth-tog">
          <button
            type="button"
            className={`mth wf${c.methodology === 'Waterfall' ? ' on' : ''}`}
            title="Sequential, gated phases"
            onClick={() => onMethodology('Waterfall')}
          >
            Waterfall
          </button>
          <button
            type="button"
            className={`mth ag${c.methodology === 'Agile' ? ' on' : ''}`}
            title="Iterative, MVP waves"
            onClick={() => onMethodology('Agile')}
          >
            Agile
          </button>
          {/*
            THREE-WAY, not two — change record §4.1. "None" says this initiative has no
            delivery lifecycle at all: onboarding people, a contracting window, a single
            document. A two-button toggle could not say it, so every initiative was pushed
            through five Waterfall phases whether or not they meant anything.

            Clicking the ACTIVE button clears to "not set" — the source's rule, kept — which
            is what keeps "None" (decided) distinct from null (undecided).
          */}
          <button
            type="button"
            className={`mth none${c.methodology === 'None' ? ' on' : ''}`}
            title="No phases — one flat Delivery window"
            onClick={() => onMethodology('None')}
          >
            None
          </button>
        </span>
        {/* The "None" badge — change record §4.1. It says at a glance, without reading the
            toggle's pressed state, that this initiative is deliberately phase-less. */}
        {c.methodology === 'None' ? <span className="rd-badge none">None</span> : null}
        {/* Choosing Agile reveals its cadence right underneath, where the choice was made. */}
        {c.methodology === 'Agile' ? (
          <div className="ag-cfg">
            <label>
              Sprint{' '}
              <input
                type="number"
                min={1}
                max={12}
                defaultValue={sw}
                onChange={(e) => onCadence(Number(e.target.value) || 2, spm)}
              />{' '}
              wks
            </label>
            <label>
              Sprints/MVP{' '}
              <input
                type="number"
                min={1}
                max={20}
                defaultValue={spm}
                onChange={(e) => onCadence(sw, Number(e.target.value) || 4)}
              />
            </label>
            <span className="ag-sum">
              MVP = {spm * sw} wks
              {c.delivery_project_id
                ? c.agile?.mvpCount
                  ? ` · ${c.agile.mvpCount} MVPs`
                  : ''
                : ' · not saved until generated'}
            </span>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * One combined Gantt for a whole project, drawn on a canvas — the source's approach.
 *
 * A canvas rather than DOM bars because a programme can hold dozens of opportunities × five
 * phases (or many more sprints), and that many absolutely-positioned divs is what makes a
 * timeline feel slow to open.
 */
function ProjectGantt({ items }: { items: Candidate[] }) {
  const [zoom, setZoom] = useState<'fit' | number>('fit');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const withPhases = useMemo(() => items.filter((c) => segsOf(c).length), [items]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !withPhases.length) return;

    const all = withPhases.flatMap((c) => segsOf(c));
    const starts = all.map((s) => pd(s.start_date)).filter((x): x is number => x !== null);
    const ends = all.map((s) => pd(s.end_date)).filter((x): x is number => x !== null);
    if (!starts.length || !ends.length) return;

    const minDate = Math.min(...starts);
    const maxDate = Math.max(...ends);
    const totalDays = Math.max(1, Math.round((maxDate - minDate) / 86400000) + 1);

    const headerH = 22;
    const rowH = 26;
    const barH = 12;
    const scrollW = scrollRef.current?.clientWidth ?? 700;
    /* The source's `ganttPPD`: fit uses the available width, a zoom level is 6px/day scaled. */
    const ppd =
      zoom === 'fit'
        ? Math.max(0.25, (scrollW > 40 ? scrollW - 2 : 700) / totalDays)
        : 6 * (Number(zoom) / 100);

    const canvasW = Math.max(40, Math.ceil(totalDays * ppd));
    const canvasH = headerH + withPhases.length * rowH;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasW * dpr;
    canvas.height = canvasH * dpr;
    canvas.style.width = `${canvasW}px`;
    canvas.style.height = `${canvasH}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvasW, canvasH);

    /* Month grid + labels across the header. */
    ctx.font = '10px system-ui, sans-serif';
    for (let d = 0; d <= totalDays; d++) {
      const day = new Date(minDate + d * 86400000);
      if (day.getUTCDate() === 1 || d === 0) {
        const x = d * ppd;
        ctx.strokeStyle = '#eceff1';
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasH);
        ctx.stroke();
        ctx.fillStyle = '#90a4ae';
        ctx.fillText(
          day.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
          x + 3,
          12,
        );
      }
    }

    withPhases.forEach((c, i) => {
      const y = headerH + i * rowH;
      for (const seg of segsOf(c)) {
        const s = pd(seg.start_date);
        const e = pd(seg.end_date);
        if (s === null || e === null) continue;
        const sOff = (s - minDate) / 86400000;
        const eOff = (e - minDate) / 86400000;
        const px = sOff * ppd;
        const pw = Math.max((eOff - sOff + 1) * ppd, 2);
        const barY = y + (rowH - barH) / 2;
        ctx.fillStyle = seg.color ?? PHASE_COLORS[seg.name ?? ''] ?? '#bbb';
        ctx.fillRect(px, barY, pw, barH);
        /* Weekends greyed INSIDE the bar, but only when a day is wide enough to see —
           below 1.5px/day it is noise. The source's own threshold. */
        if (ppd >= 1.5) {
          for (let dOff = Math.floor(sOff); dOff < Math.ceil(eOff) + 1; dOff++) {
            if (isWeekend(minDate + dOff * 86400000)) {
              const cx = dOff * ppd;
              const l = Math.max(cx, px);
              const r = Math.min(cx + ppd, px + pw);
              if (r > l) {
                ctx.fillStyle = '#d9d9d9';
                ctx.fillRect(l, barY, r - l, barH);
              }
            }
          }
        }
      }
      ctx.strokeStyle = '#eee';
      ctx.beginPath();
      ctx.moveTo(0, y + rowH);
      ctx.lineTo(canvasW, y + rowH);
      ctx.stroke();
    });

    /* Today, if it falls inside the window. */
    const today = Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
    );
    if (today >= minDate && today <= maxDate) {
      const x = ((today - minDate) / 86400000) * ppd;
      ctx.strokeStyle = '#e53935';
      ctx.beginPath();
      ctx.moveTo(x, headerH);
      ctx.lineTo(x, canvasH);
      ctx.stroke();
    }
  }, [withPhases, zoom]);

  if (!withPhases.length) {
    return (
      <div style={{ fontSize: 12, color: '#90a4ae' }}>
        No dated opportunities to chart yet — set a Planned Start and End above.
      </div>
    );
  }

  return (
    <>
      <div className="rd-gantt-zoom">
        <span>Zoom:</span>
        {(['fit', 100, 200, 400] as const).map((z) => (
          <button
            type="button"
            key={String(z)}
            className={`rd-zoom-btn${zoom === z ? ' on' : ''}`}
            onClick={() => setZoom(z)}
          >
            {z === 'fit' ? 'Fit' : `${z}%`}
          </button>
        ))}
      </div>
      <div className="rd-gantt-wrap">
        <div className="rd-gantt-side">
          <div style={{ height: 22 }} />
          {withPhases.map((c) => (
            <div
              key={c.opportunity_id}
              title={c.description ?? ''}
              style={{
                height: 26,
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                borderBottom: '1px solid #eee',
                fontSize: 11,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <strong style={{ color: '#1976d2' }}>{c.opportunity_id}</strong>
              {c.methodology === 'Agile' ? (
                <span style={{ color: '#2e7d32', fontWeight: 400, fontSize: 10 }}>
                  &nbsp;· Agile
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div className="rd-gantt-scroll" ref={scrollRef}>
          <canvas ref={canvasRef} />
        </div>
      </div>
    </>
  );
}
